import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { ProfileCardComponent } from '../../../ui/views/profile-card/profile-card.component';
import { TodayClassesComponent } from '../../../ui/views/today-classes/today-classes.component';
import { UpcomingSessionsComponent } from '../../../ui/views/upcoming-sessions/upcoming-sessions.component';
import { ProfessorAssignment } from '../../../../core/interfaces/professor';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ProfessorAcademicAppointmentService } from '../../../../core/services/academic-appointments/professor-academic-appointment.service';
import { SessionViewService } from '../../../../core/services/academic-appointments/session-view.service';
import {
  AcademicAppointment,
  displayTime,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';

/** Fenêtre de chargement du tableau de bord : aujourd'hui + les deux semaines suivantes. */
const LOOKAHEAD_DAYS = 14;

@Component({
  selector: 'app-teacher-dashboard',
  standalone: true,
  imports: [
    ProfileCardComponent,
    TranslateModule,
    TodayClassesComponent,
    UpcomingSessionsComponent,
    CommonModule,
  ],
  templateUrl: './teacher-dashboard.component.html',
  styleUrl: './teacher-dashboard.component.css',
})
export class TeacherDashboardComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly appointments = inject(ProfessorAcademicAppointmentService);
  readonly view = inject(SessionViewService);

  assignments: ProfessorAssignment[] = [];
  currentUserName = '';
  currentUserAvatar = 'assets/images/default-user.png';

  readonly sessions = signal<AcademicAppointment[]>([]);
  readonly sessionsLoading = signal(true);
  readonly sessionsErrored = signal(false);

  private readonly today = new Date().toISOString().slice(0, 10);

  readonly todaySessions = computed(() =>
    this.sessions().filter((s) => (s.scheduledDate ?? '').slice(0, 10) === this.today),
  );

  /**
   * Compteurs du profil, tous calculés sur les séances réellement planifiées :
   * séances à venir, heures d'enseignement, groupes et matières couverts.
   */
  readonly teacherStats = computed(() => {
    const list = this.sessions();
    const minutes = list.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0);
    const groups = new Set(list.map((s) => s.groupId).filter((id): id is number => !!id));
    const subjects = new Set(list.map((s) => s.subjectId).filter((id): id is number => !!id));

    return [
      { id: 1, icon: 'fa-solid fa-calendar-check mt-2', title: 'DASHBOARD.STATS.SESSIONS', points: String(list.length) },
      { id: 2, icon: 'fa-solid fa-clock mt-2', title: 'DASHBOARD.STATS.HOURS', points: String(Math.round(minutes / 60)) },
      { id: 3, icon: 'fa-solid fa-users mt-2', title: 'DASHBOARD.STATS.GROUPS', points: String(groups.size) },
      { id: 4, icon: 'fa-solid fa-book mt-2', title: 'DASHBOARD.STATS.SUBJECTS', points: String(subjects.size) },
    ];
  });

  async ngOnInit(): Promise<void> {
    await this.loadUserInfo();
    this.loadSessions();

    this.authService.assignments$.subscribe({
      next: (assignment) => (this.assignments = assignment),
      error: (err) => console.error(err),
    });
  }

  reloadSessions(): void {
    this.loadSessions();
  }

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: AcademicAppointment): string {
    return sessionStart(s);
  }

  private loadSessions(): void {
    const to = new Date();
    to.setDate(to.getDate() + LOOKAHEAD_DAYS);

    this.sessionsLoading.set(true);
    this.sessionsErrored.set(false);

    this.appointments
      .myCalendar({ from: this.today, to: to.toISOString().slice(0, 10) })
      .subscribe({
        next: (res) => {
          const list = [...(res?.data ?? [])].sort((a, b) =>
            sessionStart(a).localeCompare(sessionStart(b)),
          );
          this.sessions.set(list);
          this.sessionsLoading.set(false);
        },
        error: () => {
          // Une liste vide reste vide : aucune séance de démonstration.
          this.sessions.set([]);
          this.sessionsErrored.set(true);
          this.sessionsLoading.set(false);
        },
      });
  }

  private async loadUserInfo(): Promise<void> {
    try {
      this.currentUserName = await this.authService.getUserDisplayName();
      this.currentUserAvatar = await this.authService.getUserAvatar();
    } catch (error) {
      console.error('Error loading user info:', error);
      this.currentUserName = '';
      this.currentUserAvatar = 'assets/images/default-user.png';
    }
  }
}
